import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AddRounded, ArrowForwardRounded, DeleteOutlineRounded } from '@mui/icons-material';
import type { CourseEquivalenceDto } from '../../../lib/api/academic.api';
import type { CourseOption } from './CurriculumMapEditor';

// ─── Equivalencias entre catálogos ──────────────────────────────────────────
// Una reforma reemplaza cursos. Quien aprobó el viejo cumplió el requisito del
// nuevo, y sin decirlo la auditoría reporta el curso que cursó como fuera de
// plan Y el que lo reemplazó como pendiente: el mismo trabajo contado como
// brecha, dos veces.

interface CourseEquivalenceEditorProps {
  value: CourseEquivalenceDto[];
  onChange: (next: CourseEquivalenceDto[]) => void;
  courses: CourseOption[];
}

export function CourseEquivalenceEditor({
  value,
  onChange,
  courses,
}: CourseEquivalenceEditorProps): JSX.Element {
  const { t } = useTranslation('sis');
  const byId = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);
  const label = (c: CourseOption) => (c.code ? `${c.code} — ${c.name}` : c.name);

  const replace = (idx: number, next: CourseEquivalenceDto) =>
    onChange(value.map((row, i) => (i === idx ? next : row)));

  const picker = (
    idx: number,
    row: CourseEquivalenceDto,
    field: 'fromCourseId' | 'toCourseId',
    fieldLabel: string,
  ) => (
    <Autocomplete
      sx={{ flexGrow: 1, minWidth: 220 }}
      options={courses}
      getOptionLabel={label}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      value={byId.get(row[field]) ?? null}
      onChange={(_, selected) => replace(idx, { ...row, [field]: selected?.id ?? '' })}
      renderInput={(params) => (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <TextField {...(params as any)} label={fieldLabel} size="small" />
      )}
    />
  );

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {t('program.equivalences.helper')}
      </Typography>

      {value.map((row, idx) => {
        // A row pointing at itself would silently do nothing; said out loud.
        const isSelf = !!row.fromCourseId && row.fromCourseId === row.toCourseId;
        return (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              {picker(idx, row, 'fromCourseId', t('program.equivalences.from'))}
              <ArrowForwardRounded fontSize="small" color="action" />
              {picker(idx, row, 'toCourseId', t('program.equivalences.to'))}
              <TextField
                label={t('program.equivalences.note')}
                placeholder={t('program.equivalences.notePlaceholder')}
                value={row.note ?? ''}
                onChange={(e) => {
                  const note = e.target.value;
                  const { note: _drop, ...rest } = row;
                  void _drop;
                  replace(idx, note ? { ...rest, note } : rest);
                }}
                size="small"
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
              <Tooltip title={t('program.equivalences.remove')}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onChange(value.filter((_, i) => i !== idx))}
                >
                  <DeleteOutlineRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {isSelf && (
              <Typography variant="caption" color="error" mt={0.5} display="block">
                {t('program.equivalences.sameCourse')}
              </Typography>
            )}
          </Paper>
        );
      })}

      <Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={() => onChange([...value, { fromCourseId: '', toCourseId: '' }])}
        >
          {t('program.equivalences.add')}
        </Button>
      </Box>
    </Stack>
  );
}
